import type { ReactNode } from 'react'
import { useDroppable } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { PickerItem } from './PickerItem'
import type { ColumnId, ItemId } from './types'

export interface PickerColumnProps<T> {
  id: ColumnId
  title: string
  items: T[]
  getId: (item: T) => ItemId
  renderItem: (item: T) => ReactNode
  empty?: ReactNode
}

export function PickerColumn<T>({ id, title, items, getId, renderItem, empty }: PickerColumnProps<T>) {
  const { setNodeRef, isOver } = useDroppable({ id })
  const ids = items.map(getId)

  return (
    <div className="flex flex-col gap-2 min-h-0">
      <header className="flex items-center justify-between text-xs uppercase tracking-wide text-slate-500">
        <span>{title}</span>
        <span className="text-slate-400">{items.length}</span>
      </header>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div
          ref={setNodeRef}
          className={`flex flex-col gap-1.5 p-2 border rounded min-h-[120px] flex-1 transition-colors ${
            isOver ? 'border-sky-400 bg-sky-50/40' : 'border-slate-200 bg-slate-50/40'
          }`}
        >
          {items.length === 0 ? (
            <div className="flex items-center justify-center text-xs text-slate-400 py-4 select-none">
              {empty ?? 'Brak elementów'}
            </div>
          ) : (
            items.map(item => (
              <PickerItem key={getId(item)} id={getId(item)}>
                {renderItem(item)}
              </PickerItem>
            ))
          )}
        </div>
      </SortableContext>
    </div>
  )
}
